import React, {useEffect, useMemo, useState} from "react";
import { Link } from "react-router-dom";
import useApiStable from "../../utils/useApiStable";
import {sortTournamentsForOverview} from "./tournamentViewUtils";
import {exportGroupTablesPdf} from "./exportGroupTablesPdf";
import {downloadFileFromApi} from "../../utils/downloadFile";
import "../../styles/GroupPhase.css";
import "../../styles/AdminPanel.css";
import KnockoutSeedConfig from "./KnockoutSeedConfig";

const getTeamLabel = (team) => {
  if (!team) return "";
  if (team.displayName) return team.displayName;
  if (team.name && team.name.trim()) return team.name;
  if (Array.isArray(team.players) && team.players.length > 0) {
    return team.players.map((player) => player.name).join(" / ");
  }
  return `Team ${team.id}`;
};

const getTeamPlayerLabel = (teamPlayer) => teamPlayer?.name || `TeamPlayer ${teamPlayer?.id ?? "?"}`;

const toDateInputValue = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const TeamList = ({teams, teamSize, onRemove, onRename}) => {
  const [editingId, setEditingId] = React.useState(null);
  const [editName, setEditName] = React.useState("");

  const startEdit = (team) => {
    setEditingId(team.id);
    setEditName(team.name || "");
  };

  const cancelEdit = () => setEditingId(null);

  const confirmEdit = (teamId) => {
    onRename(teamId, editName);
    setEditingId(null);
  };

  const complete = teams.filter((t) => (t.players || []).length >= teamSize);
  const incomplete = teams.filter((t) => (t.players || []).length < teamSize);

  const renderTeam = (team, isIncomplete) => (
    <li
      key={team.id}
      className="adminpanel-chip-row"
      style={{alignItems: "flex-start", borderLeft: isIncomplete ? "3px solid #e65100" : undefined, paddingLeft: isIncomplete ? 8 : undefined}}
    >
      <div style={{display: "flex", flexDirection: "column", gap: 4, flex: 1}}>
        {editingId === team.id ? (
          <div style={{display: "flex", gap: 6, alignItems: "center"}}>
            <input
              className="groupphase-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmEdit(team.id); if (e.key === "Escape") cancelEdit(); }}
              autoFocus
              style={{fontSize: 13}}
            />
            <button className="groupphase-button" onClick={() => confirmEdit(team.id)}>Speichern</button>
            <button className="groupphase-button secondary" onClick={cancelEdit}>Abbrechen</button>
          </div>
        ) : (
          <span className="adminpanel-chip">{getTeamLabel(team)}</span>
        )}
        <small style={{color: isIncomplete ? "#e65100" : "#666"}}>
          {(team.players || []).map((p) => p.name).join(" / ") || "Keine TeamPlayer"}
          {isIncomplete && ` (${(team.players || []).length}/${teamSize} Spieler)`}
        </small>
      </div>
      {editingId !== team.id && (
        <div style={{display: "flex", gap: 4}}>
          <button
            className="groupphase-button secondary"
            title="Teamname bearbeiten"
            onClick={() => startEdit(team)}
          >
            Umbenennen
          </button>
          <button
            className="groupphase-button adminpanel-chip-remove"
            title="Team aus Turnier entfernen"
            onClick={() => onRemove(team.id)}
          >
            Entfernen
          </button>
        </div>
      )}
    </li>
  );

  return (
    <div>
      {incomplete.length > 0 && (
        <>
          <p className="adminpanel-warning-text" style={{marginBottom: 6}}>
            ⚠ Unvollständige Teams ({incomplete.length})
          </p>
          <ul className="groupphase-list adminpanel-compact-list" style={{marginBottom: 12}}>
            {incomplete.map((t) => renderTeam(t, true))}
          </ul>
        </>
      )}
      {complete.length > 0 && (
        <>
          {incomplete.length > 0 && (
            <p style={{fontSize: 12, color: "#555", marginBottom: 6}}>Vollständige Teams ({complete.length})</p>
          )}
          <ul className="groupphase-list adminpanel-compact-list">
            {complete.map((t) => renderTeam(t, false))}
          </ul>
        </>
      )}
      {teams.length === 0 && (
        <p className="adminpanel-hint">Noch keine Teams angemeldet.</p>
      )}
    </div>
  );
};

const TournamentManagement = () => {
  const api = useApiStable();
  const [tournaments, setTournaments] = useState([]);
  const [newTournamentName, setNewTournamentName] = useState("");
  const [newTournamentYear, setNewTournamentYear] = useState(() => new Date().getFullYear());
  const [newTournamentTeamSize, setNewTournamentTeamSize] = useState(1);
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [tournamentTeams, setTournamentTeams] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [knockoutPhaseExists, setKnockoutPhaseExists] = useState({});
  const [knockoutBracketPlanned, setKnockoutBracketPlanned] = useState({});
  const [groupAssignmentError, setGroupAssignmentError] = useState("");
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [courtGroups, setCourtGroups] = useState([]);
  const [selectedCourtGroupId, setSelectedCourtGroupId] = useState("");
  const [isCreateTournamentModalOpen, setIsCreateTournamentModalOpen] = useState(false);
  const [distributionMessage, setDistributionMessage] = useState("");
  const [distributionError, setDistributionError] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [selectedTeamPlayerIds, setSelectedTeamPlayerIds] = useState([]);
  const [teamPlayerSearch, setTeamPlayerSearch] = useState("");
  const [selectedTeamToAddToGroup, setSelectedTeamToAddToGroup] = useState("");
  const [newTournamentNumberOfGroups, setNewTournamentNumberOfGroups] = useState(4);
  const [newTournamentRegistrationDeadline, setNewTournamentRegistrationDeadline] = useState("");
  const [newTournamentEndDate, setNewTournamentEndDate] = useState("");
  const [editTournamentData, setEditTournamentData] = useState({
    name: "",
    year: new Date().getFullYear(),
    description: "",
    courtGroupId: "",
    teamSize: 1,
    registrationDeadline: "",
    endDate: "",
  });
  const [savingTournament, setSavingTournament] = useState(false);

  const fetchTournamentStatuses = (list) => {
    const safeList = Array.isArray(list) ? list : [];
    const statusPromises = safeList.map((tournament) =>
      api
        .get(`/tournaments/knockout/${tournament.id}/exists`)
        .then((exists) => ({ tournamentId: tournament.id, exists }))
        .catch(() => ({ tournamentId: tournament.id, exists: false }))
    );
    const plannedPromises = safeList.map((tournament) =>
      api
        .get(`/tournaments/knockout/${tournament.id}/planned`)
        .then((planned) => ({ tournamentId: tournament.id, planned }))
        .catch(() => ({ tournamentId: tournament.id, planned: false }))
    );
    Promise.all(statusPromises).then((statuses) => {
      const statusMap = {};
      statuses.forEach((status) => {
        statusMap[status.tournamentId] = status.exists;
      });
      setKnockoutPhaseExists(statusMap);
    });
    Promise.all(plannedPromises).then((statuses) => {
      const plannedMap = {};
      statuses.forEach((status) => {
        plannedMap[status.tournamentId] = status.planned;
      });
      setKnockoutBracketPlanned(plannedMap);
    });
  };

  const fetchTournaments = () => {
    api
      .get("/tournaments")
      .then((data) => {
        const list = sortTournamentsForOverview(Array.isArray(data) ? data : []);
        setTournaments(list);
        fetchTournamentStatuses(list);
      })
      .catch(() => setTournaments([]));
  };

  const fetchTeamPlayers = () => {
    api
      .get("/team-players")
      .then((data) => setTeamPlayers(Array.isArray(data) ? data : []))
      .catch(() => setTeamPlayers([]));
  };

  const fetchSelectedTournamentData = (tournamentId = selectedTournamentId) => {
    if (!tournamentId) {
      setGroups([]);
      setTournamentTeams([]);
      return Promise.resolve();
    }

    return Promise.all([
      api
        .get(`/tournaments/groups?tournamentId=${tournamentId}`)
        .then((data) => setGroups(Array.isArray(data) ? data : []))
        .catch(() => setGroups([])),
      api
        .get(`/tournaments/${tournamentId}/teams`)
        .then((data) => setTournamentTeams(Array.isArray(data) ? data : []))
        .catch(() => setTournamentTeams([])),
    ]);
  };

  useEffect(() => {
    fetchTournaments();
  }, [api]);

  useEffect(() => {
    fetchTeamPlayers();
  }, [api]);

  useEffect(() => {
    api
      .get("/courts/courtGroups")
      .then((data) => setCourtGroups(Array.isArray(data) ? data : []))
      .catch(() => setCourtGroups([]));
  }, [api]);

  useEffect(() => {
    setSelectedGroupId(null);
    setGroupAssignmentError("");
    setSelectedTeamPlayerIds([]);
    setNewTeamName("");
    setSelectedTeamToAddToGroup("");
    fetchSelectedTournamentData(selectedTournamentId);
  }, [selectedTournamentId, api]);

  const selectedTournament = useMemo(
    () => tournaments.find((t) => t.id === selectedTournamentId) || null,
    [tournaments, selectedTournamentId]
  );

  // Summe der (ggf. individuellen) Qualifikanten aller Gruppen - bestimmt u.a., ob der
  // Turnierbaum ein Halbfinale enthält (ab 4) und damit ein Spiel um Platz 3 möglich ist. Wird
  // von KnockoutSeedConfig beim Laden/Ändern der Seed-Konfiguration aktuell gehalten (siehe
  // onBracketSizeChange), da die Qualifikanten-Anzahl inzwischen pro Gruppe individuell ist.
  const [bracketSizeOfSelectedTournament, setBracketSizeOfSelectedTournament] = useState(0);
  // Genau 2 Gruppen mit je genau 1 Qualifikant ("Direktfinale-Format", siehe
  // KnockoutSeedService#isDirectFinalFormat): dort ist ein Spiel um Platz 3 auch OHNE Halbfinale
  // möglich (Gruppenzweite spielen direkt gegeneinander, obwohl sie keine "Qualifikanten" sind).
  // Ohne dieses zweite Flag würde die Checkbox unten trotz bracketSize=2 fälschlich als
  // "nicht möglich" erscheinen (die < 4-Prüfung allein reicht für diesen Sonderfall nicht aus).
  const [directFinalFormatEligibleOfSelectedTournament, setDirectFinalFormatEligibleOfSelectedTournament] = useState(false);

  useEffect(() => {
    setBracketSizeOfSelectedTournament(0);
    setDirectFinalFormatEligibleOfSelectedTournament(false);
  }, [selectedTournamentId]);

  useEffect(() => {
    if (!selectedTournament) {
      setEditTournamentData({
        name: "",
        year: new Date().getFullYear(),
        description: "",
        courtGroupId: "",
        teamSize: 1,
        registrationDeadline: "",
        endDate: "",
      });
      return;
    }

    setEditTournamentData({
      name: selectedTournament.name || "",
      year: selectedTournament.year || new Date().getFullYear(),
      description: selectedTournament.description || "",
      courtGroupId: selectedTournament.courtGroupId ? String(selectedTournament.courtGroupId) : "",
      teamSize: selectedTournament.teamSize || 1,
      registrationDeadline: toDateInputValue(selectedTournament.registrationDeadline),
      endDate: toDateInputValue(selectedTournament.endDate),
    });
  }, [selectedTournament]);

  const selectedTournamentTeamSize = Math.max(1, Number(selectedTournament?.teamSize || 1));
  const isSingles = selectedTournamentTeamSize === 1;
  const teamLabel = isSingles ? "Spieler" : "Team";
  const teamsLabel = isSingles ? "Spieler" : "Teams";

  const assignedTeamPlayerIds = useMemo(() => {
    const assigned = new Set();
    tournamentTeams.forEach((team) => {
      (team.players || []).forEach((teamPlayer) => {
        if (teamPlayer?.id != null) {
          assigned.add(teamPlayer.id);
        }
      });
    });
    return assigned;
  }, [tournamentTeams]);

  const availableTeamPlayers = useMemo(
    () => teamPlayers
      .filter((teamPlayer) => !assignedTeamPlayerIds.has(teamPlayer.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")),
    [teamPlayers, assignedTeamPlayerIds]
  );

  const teamsAssignedToGroups = useMemo(() => {
    const assigned = new Set();
    (Array.isArray(groups) ? groups : []).forEach((group) => {
      (group.teamIds || group.playerIds || []).forEach((teamId) => assigned.add(teamId));
    });
    return assigned;
  }, [groups]);

  const unassignedTournamentTeams = useMemo(
    () => tournamentTeams.filter((team) => !teamsAssignedToGroups.has(team.id)),
    [tournamentTeams, teamsAssignedToGroups]
  );

  const selectedGroup = useMemo(
    () => (Array.isArray(groups) ? groups : []).find((group) => group.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  const allGroupsCompleted = useMemo(
    () => groups.length > 0 && groups.every((group) => group.completed),
    [groups]
  );

  const groupPhaseClosed = useMemo(
    () => groups.length > 0 && groups.every((group) => group.closed),
    [groups]
  );

  const { totalGroupMatchCount, openGroupMatchCount } = useMemo(() => {
    const total = groups.reduce((sum, g) => sum + (g.totalMatchCount || 0), 0);
    const played = groups.reduce((sum, g) => sum + (g.playedMatchCount || 0), 0);
    return { totalGroupMatchCount: total, openGroupMatchCount: total - played };
  }, [groups]);

  const getAvailableTeamsForGroup = (group) => {
    const groupTeamIds = group?.teamIds || group?.playerIds || [];
    return tournamentTeams.filter((team) => {
      if (groupTeamIds.includes(team.id)) return false;
      return !teamsAssignedToGroups.has(team.id);
    });
  };

  const createTournament = async () => {
    const trimmedName = newTournamentName.trim();
    const yearNumber = Number(newTournamentYear);
    const teamSize = Math.max(1, Number(newTournamentTeamSize || 1));

    if (!trimmedName || Number.isNaN(yearNumber)) return false;
    if (!selectedCourtGroupId) {
      alert("Bitte eine Platzgruppe auswählen.");
      return false;
    }
    if (!newTournamentEndDate) {
      alert("Bitte ein Enddatum angeben.");
      return false;
    }

    try {
      const tournament = await api.post("/tournaments", {
        name: trimmedName,
        year: yearNumber,
        courtGroupId: Number(selectedCourtGroupId),
        teamSize,
        numberOfGroups: newTournamentNumberOfGroups,
        registrationDeadline: newTournamentRegistrationDeadline || null,
        endDate: newTournamentEndDate,
      });
      setTournaments((prev) => {
        const updated = [...prev, tournament];
        fetchTournamentStatuses(updated);
        return updated;
      });
      setSelectedTournamentId(tournament.id);
      setSelectedGroupId(null);
      setNewTournamentName("");
      setNewTournamentYear(new Date().getFullYear());
      setNewTournamentTeamSize(1);
      setSelectedCourtGroupId("");
      setNewTournamentRegistrationDeadline("");
      setNewTournamentEndDate("");
      return true;
    } catch (error) {
      alert(error.message || "Turnier konnte nicht angelegt werden.");
      return false;
    }
  };

  const deleteTournament = (id) => {
    api
      .delete(`/tournaments/${id}`)
      .then(() => {
        setTournaments((prev) => prev.filter((t) => t.id !== id));
        if (selectedTournamentId === id) setSelectedTournamentId(null);
      })
      .catch((err) => alert(err.message));
  };

  const createGroup = () => {
    if (!newGroupName.trim() || !selectedTournamentId) return;
    api
      .post("/tournaments/groups", {
        name: newGroupName,
        tournamentId: selectedTournamentId,
      })
      .then((group) => {
        setGroups((prev) => [...prev, group]);
        setSelectedGroupId(group.id);
        setNewGroupName("");
      });
  };

  const deleteGroup = (id) => {
    api
      .delete(`/tournaments/groups/${id}`)
      .then(() => {
        setGroups((prev) => prev.filter((g) => g.id !== id));
        if (selectedGroupId === id) setSelectedGroupId(null);
      })
      .catch((err) => alert(err.message));
  };

  const toggleTeamPlayerSelection = (teamPlayerId) => {
    setSelectedTeamPlayerIds((prev) => {
      if (prev.includes(teamPlayerId)) {
        return prev.filter((id) => id !== teamPlayerId);
      }
      if (prev.length >= selectedTournamentTeamSize) {
        alert(`Es können maximal ${selectedTournamentTeamSize} TeamPlayer ausgewählt werden.`);
        return prev;
      }
      return [...prev, teamPlayerId];
    });
  };

  const createTeamForTournament = async () => {
    if (!selectedTournamentId) return;
    if (selectedTeamPlayerIds.length !== selectedTournamentTeamSize) {
      alert(
        selectedTournamentTeamSize === 1
          ? "Bitte genau einen TeamPlayer auswählen."
          : `Bitte genau ${selectedTournamentTeamSize} TeamPlayer auswählen.`
      );
      return;
    }

    const selectedPlayers = selectedTeamPlayerIds
      .map((id) => teamPlayers.find((teamPlayer) => teamPlayer.id === id))
      .filter(Boolean);

    const defaultTeamName = selectedPlayers.map((teamPlayer) => teamPlayer.name).join(" / ");

    try {
      const team = await api.post("/teams", {
        name: newTeamName.trim() || defaultTeamName,
        players: selectedPlayers.map((teamPlayer) => ({ id: teamPlayer.id })),
      });
      await api.post(`/tournaments/${selectedTournamentId}/add-team/${team.id}`);
      await fetchSelectedTournamentData(selectedTournamentId);
      fetchTournaments();
      setNewTeamName("");
      setSelectedTeamPlayerIds([]);
    } catch (error) {
      alert(error.message || "Team konnte nicht erstellt werden.");
    }
  };

  const renameTeam = async (teamId, newName) => {
    try {
      await api.patch(`/teams/${teamId}`, { name: newName });
      await fetchSelectedTournamentData(selectedTournamentId);
    } catch (err) {
      alert(err.message || "Teamname konnte nicht gespeichert werden.");
    }
  };

  const removeTeamFromTournament = async (teamId) => {
    const isAssignedToGroup = groups.some((group) => (group.teamIds || group.playerIds || []).includes(teamId));
    if (isAssignedToGroup) {
      alert("Bitte das Team zuerst aus allen Gruppen entfernen.");
      return;
    }

    try {
      await api.delete(`/tournaments/${selectedTournamentId}/remove-team/${teamId}`);
      await fetchSelectedTournamentData(selectedTournamentId);
      fetchTournaments();
    } catch (err) {
      alert(err.message);
    }
  };

  const addTeamToGroup = (groupId, teamId) => {
    api
      .post(`/tournaments/groups/${groupId}/add-team/${teamId}`)
      .then((updatedGroup) => {
        setGroups((prev) => prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)));
        setGroupAssignmentError("");
        setSelectedTeamToAddToGroup("");
      })
      .catch((err) => {
        setGroupAssignmentError(err.message || "Team konnte nicht hinzugefügt werden.");
      });
  };

  const removeTeamFromGroup = (groupId, teamId) => {
    api
      .delete(`/tournaments/groups/${groupId}/remove-team/${teamId}`)
      .then((updatedGroup) => {
        setGroups((prev) => prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)));
        setGroupAssignmentError("");
      })
      .catch((err) => {
        setGroupAssignmentError(err.message || "Team konnte nicht entfernt werden.");
      });
  };

  const handleTournamentSelect = (tournamentId) => {
    setSelectedTournamentId(tournamentId);
    setSelectedGroupId(null);
    setGroupAssignmentError("");
  };

  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
    setGroupAssignmentError("");
    setSelectedTeamToAddToGroup("");
  };

  const handleListItemKeyDown = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  const reopenGroupPhase = async (tournamentId) => {
    const tournament = tournaments.find((t) => t.id === tournamentId);
    const confirmed = window.confirm(
      `Möchten Sie die K.O.-Phase für "${tournament?.name}" wirklich löschen?\n\n` +
        "Dadurch wird die Gruppenphase wieder geöffnet und alle K.O.-Match-Ergebnisse gehen verloren.\n\n" +
        "Diese Aktion kann nicht rückgängig gemacht werden!"
    );
    if (!confirmed) return;
    try {
      await api.delete(`/tournaments/knockout/${tournamentId}`);
      setKnockoutPhaseExists((prev) => ({ ...prev, [tournamentId]: false }));
      setKnockoutBracketPlanned((prev) => ({ ...prev, [tournamentId]: false }));
      alert(`Gruppenphase für "${tournament?.name}" wurde erfolgreich wieder geöffnet!`);
    } catch (error) {
      alert("Fehler beim Öffnen der Gruppenphase: " + error.message);
    }
  };

  const exportPlayersAsPdf = async () => {
    if (!selectedTournamentId) return;
    try {
      await downloadFileFromApi(
        api,
        `/tournaments/${selectedTournamentId}/export/players.pdf`,
        `teilnehmerliste-${selectedTournamentId}.pdf`
      );
    } catch (error) {
      alert(error.message || "PDF-Export ist fehlgeschlagen.");
    }
  };

  const distributeTeams = (onlyUnassigned = false) => {
    if (!selectedTournamentId) return;
    setDistributionMessage("");
    setDistributionError(false);
    api
      .post(`/tournaments/${selectedTournamentId}/distribute-teams`, { onlyUnassigned })
      .then((updatedGroups) => {
        setGroups(updatedGroups);
        setDistributionMessage(
          onlyUnassigned
            ? "Freie Teams wurden erfolgreich auf die Gruppen verteilt."
            : "Alle Teams wurden erfolgreich neu auf die Gruppen verteilt."
        );
        fetchSelectedTournamentData(selectedTournamentId);
      })
      .catch((error) => {
        setDistributionError(true);
        setDistributionMessage("Fehler bei der Verteilung der Teams: " + error.message);
      });
  };

  const updateNumberOfGroups = async (tournamentId, newCount) => {
    try {
      const updated = await api.patch(`/tournaments/${tournamentId}/numberOfGroups`, { numberOfGroups: newCount });
      setTournaments((prev) => prev.map((t) => (t.id === tournamentId ? updated : t)));
      const groupData = await api.get(`/tournaments/groups?tournamentId=${tournamentId}`);
      setGroups(Array.isArray(groupData) ? groupData : []);
    } catch (err) {
      alert("Fehler beim Ändern der Gruppenanzahl: " + err.message);
    }
  };

  const updateQualifiersPerGroup = async (tournamentId, newCount) => {
    try {
      const updated = await api.patch(`/tournaments/${tournamentId}/qualifiersPerGroup`, { qualifiersPerGroup: newCount });
      setTournaments((prev) => prev.map((t) => (t.id === tournamentId ? updated : t)));
    } catch (err) {
      alert("Fehler beim Ändern der Qualifikanten pro Gruppe: " + err.message);
      throw err;
    }
  };

  const updateThirdPlaceMatch = async (tournamentId, enabled) => {
    try {
      const updated = await api.patch(`/tournaments/${tournamentId}/thirdPlaceMatch`, { thirdPlaceMatch: enabled });
      setTournaments((prev) => prev.map((t) => (t.id === tournamentId ? updated : t)));
    } catch (err) {
      alert("Fehler beim Ändern des Spiels um Platz 3: " + err.message);
    }
  };

  // Löscht alle K.O.-Matches und legt sie sofort wieder anhand der aktuellen Seed-Konfiguration an.
  // Im Unterschied zu "Gruppenphase wieder öffnen" bleiben abgeschlossene Gruppen abgeschlossen -
  // deren Teams werden direkt wieder in die neuen Matches eingetragen. Nur eine Rückfrage, wenn
  // bereits ein K.O.-Ergebnis erfasst wurde.
  const recreateKnockoutMatches = async (tournamentId) => {
    const tournament = tournaments.find((t) => t.id === tournamentId);
    try {
      const matches = await api.get(`/tournaments/knockout/${tournamentId}/matches`);
      const hasResults = matches.some((m) => m.completed);
      if (hasResults) {
        const confirmed = window.confirm(
          `Für "${tournament?.name}" sind bereits K.O.-Ergebnisse eingetragen.\n\n` +
            "Wenn Sie fortfahren, werden ALLE K.O.-Spiele gelöscht und anhand der aktuellen " +
            "Seed-Konfiguration neu angelegt. Eingetragene K.O.-Ergebnisse gehen dabei verloren.\n\n" +
            "Fortfahren?"
        );
        if (!confirmed) return;
      }
      await api.post(`/tournaments/knockout/${tournamentId}/recreate`);
      alert(`K.O.-Spiele für "${tournament?.name}" wurden neu angelegt.`);
    } catch (error) {
      alert("Fehler beim Neuanlegen der K.O.-Spiele: " + error.message);
    }
  };

  const startTournament = async (tournamentId) => {
    try {
      const updated = await api.post(`/tournaments/${tournamentId}/start`);
      setTournaments((prev) => prev.map((t) => (t.id === tournamentId ? updated : t)));
      const groupData = await api.get(`/tournaments/groups?tournamentId=${tournamentId}`);
      setGroups(Array.isArray(groupData) ? groupData : []);
      setKnockoutPhaseExists((prev) => ({ ...prev, [tournamentId]: false }));
      // Turnierstart baut den K.O.-Baum nicht mehr automatisch (siehe TournamentPlanningService).
      setKnockoutBracketPlanned((prev) => ({ ...prev, [tournamentId]: false }));
    } catch (err) {
      alert("Fehler beim Starten des Turniers: " + err.message);
    }
  };

  const closeGroupPhase = async (tournamentId) => {
    const tournament = tournaments.find((t) => t.id === tournamentId);
    const confirmMessage = allGroupsCompleted
      ? `Möchten Sie die Gruppenphase für "${tournament?.name}" wirklich abschließen und zur K.O.-Phase wechseln?`
      : `Achtung: ${openGroupMatchCount} von ${totalGroupMatchCount} Gruppenspielen haben noch kein Ergebnis. ` +
        `Diese Spiele zählen nicht in die Tabelle ein, wodurch die K.O.-Platzierungen auf unvollständigen ` +
        `Ergebnissen beruhen können. Gruppenphase für "${tournament?.name}" trotzdem abschließen und zur ` +
        `K.O.-Phase wechseln?`;
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      await api.post(`/tournaments/${tournamentId}/close-group-phase?force=true`);
      await fetchSelectedTournamentData(tournamentId);
      fetchTournaments();
      setKnockoutPhaseExists((prev) => ({ ...prev, [tournamentId]: true }));
      setKnockoutBracketPlanned((prev) => ({ ...prev, [tournamentId]: true }));
      window.location.href = `/knockout-phase?tournamentId=${tournamentId}`;
    } catch (error) {
      alert("Fehler beim Abschließen der Gruppenphase: " + error.message);
    }
  };

  const buildKnockoutBracket = async (tournamentId) => {
    try {
      await api.post(`/tournaments/${tournamentId}/backfill-knockout-bracket`);
      setKnockoutBracketPlanned((prev) => ({ ...prev, [tournamentId]: true }));
      alert("K.O.-Baum wurde erfolgreich angelegt.");
    } catch (error) {
      alert("Fehler beim Anlegen des K.O.-Baums: " + error.message);
    }
  };

  // Löscht den K.O.-Baum, solange die Gruppenphase noch offen ist (Baum nur "geplant", KO-Phase
  // noch nicht aktiv). Nutzt denselben Endpoint wie "Gruppenphase wieder öffnen", aber mit
  // angepasstem Bestätigungstext, da in diesem Zustand keine echten KO-Ergebnisse existieren
  // können - stattdessen werden nach dem Löschen Gruppen wieder änderbar.
  const deleteKnockoutBracket = async (tournamentId) => {
    const tournament = tournaments.find((t) => t.id === tournamentId);
    const confirmed = window.confirm(
      `Möchten Sie den K.O.-Baum für "${tournament?.name}" wirklich löschen?\n\n` +
        "Gruppen (Team-Zuordnung, Qualifikanten, Seeding) können danach wieder geändert werden.\n\n" +
        "Fortfahren?"
    );
    if (!confirmed) return;
    try {
      await api.delete(`/tournaments/knockout/${tournamentId}`);
      setKnockoutPhaseExists((prev) => ({ ...prev, [tournamentId]: false }));
      setKnockoutBracketPlanned((prev) => ({ ...prev, [tournamentId]: false }));
      alert(`K.O.-Baum für "${tournament?.name}" wurde gelöscht.`);
    } catch (error) {
      alert("Fehler beim Löschen des K.O.-Baums: " + error.message);
    }
  };

  const saveTournamentData = async () => {
    if (!selectedTournamentId) return;
    if (!editTournamentData.name.trim()) {
      alert("Bitte einen Turniernamen angeben.");
      return;
    }
    if (!editTournamentData.courtGroupId) {
      alert("Bitte eine Platzgruppe auswählen.");
      return;
    }
    if (!editTournamentData.endDate) {
      alert("Bitte ein Enddatum angeben.");
      return;
    }

    try {
      setSavingTournament(true);
      const updated = await api.put(`/tournaments/${selectedTournamentId}`, {
        name: editTournamentData.name.trim(),
        year: Number(editTournamentData.year),
        description: editTournamentData.description?.trim() || null,
        courtGroupId: Number(editTournamentData.courtGroupId),
        teamSize: Number(editTournamentData.teamSize || 1),
        numberOfGroups: selectedTournament?.numberOfGroups || newTournamentNumberOfGroups,
        registrationDeadline: editTournamentData.registrationDeadline || null,
        endDate: editTournamentData.endDate,
      });
      setTournaments((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      alert("Turnierdaten wurden gespeichert.");
    } catch (error) {
      alert(error.message || "Turnierdaten konnten nicht gespeichert werden.");
    } finally {
      setSavingTournament(false);
    }
  };

  return (
    <div className="adminpanel-container adminpanel-container--wide">
      <div className="adminpanel-page-header">
        <div>
          <p className="adminpanel-eyebrow">Turniere</p>
          <h1 className="adminpanel-title">Turnierverwaltung</h1>
          <p className="adminpanel-page-description">
            Verwalten Sie Turniere, Gruppen und Teams in der Reihenfolge des fachlichen Ablaufs:
            erst Turnier wählen, dann Teams anlegen, Gruppen pflegen und zuletzt das Turnier starten.
            Der Spielerpool wird separat im Adminbereich gepflegt.
          </p>
        </div>
        <div className="adminpanel-page-actions">
          <Link to="/tournaments" className="adminpanel-link-button secondary">
            Zur Gruppenphase
          </Link>
          <Link to="/admin" className="adminpanel-link-button secondary">
            Zum Adminbereich / Spielerpool
          </Link>
        </div>
      </div>

      <section className="adminpanel-card adminpanel-help-card">
        <h2>Schnellstart</h2>
        <div className="adminpanel-help-grid">
          <div className="adminpanel-help-step">
            <strong>1. Turnier wählen</strong>
            <span>Links ein bestehendes Turnier auswählen oder oben neu anlegen.</span>
          </div>
          <div className="adminpanel-help-step">
            <strong>2. Teams anlegen</strong>
            <span>Rechts TeamPlayer auswählen und daraus Turnierteams erzeugen.</span>
          </div>
          <div className="adminpanel-help-step">
            <strong>3. Gruppen füllen</strong>
            <span>Teams manuell zuordnen oder automatisch auf Gruppen verteilen.</span>
          </div>
          <div className="adminpanel-help-step">
            <strong>4. Turnier starten</strong>
            <span>Gruppenanzahl prüfen und anschließend die Gruppenphase initialisieren.</span>
          </div>
        </div>
      </section>

      {selectedTournament && (
        <section className="adminpanel-card adminpanel-selection-summary">
          <div>
            <p className="adminpanel-eyebrow">Aktives Turnier</p>
            <h2>
              {selectedTournament.name} {selectedTournament.year ? `(${selectedTournament.year})` : ""}
            </h2>
          </div>
          <div className="adminpanel-summary-meta">
            {selectedTournamentTeamSize > 1 && !selectedTournament.started ? (
              <>
                <span className="adminpanel-badge" style={{color: "#2e7d32"}}>
                  {selectedTournament.numberOfCompleteTeams ?? 0} vollständig
                </span>
                {(selectedTournament.numberOfIncompleteTeams ?? 0) > 0 && (
                  <span className="adminpanel-badge" style={{color: "#bf360c"}}>
                    {selectedTournament.numberOfIncompleteTeams} unvollständig
                  </span>
                )}
              </>
            ) : (
              <span className="adminpanel-badge">{selectedTournament.numberOfTeams} {teamsLabel}</span>
            )}
            {!isSingles && <span className="adminpanel-badge">{selectedTournamentTeamSize} Spieler/Team</span>}
            <span className="adminpanel-badge">
              {selectedTournament.numberOfGroups || 0} Gruppen
            </span>
          </div>
          <button
            type="button"
            className="groupphase-button secondary"
            disabled={tournamentTeams.length === 0}
            onClick={exportPlayersAsPdf}
          >
            Teilnehmerliste als PDF
          </button>
        </section>
      )}

      <div className="adminpanel-layout">
          <div className="adminpanel-column adminpanel-column--left">
            <section className="adminpanel-card">
              <div className="adminpanel-card-header">
                <h2>Turnier auswählen</h2>
                <button
                  type="button"
                  className="groupphase-button adminpanel-add-button"
                  title="Neues Turnier anlegen"
                  onClick={() => setIsCreateTournamentModalOpen(true)}
                >
                  +
                </button>
              </div>
              <ul className="adminpanel-list adminpanel-list--compact">
                {tournaments.map((t) => (
                  <li
                    key={t.id}
                    className={`adminpanel-list-item ${selectedTournamentId === t.id ? "active" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleTournamentSelect(t.id)}
                    onKeyDown={(event) => handleListItemKeyDown(event, () => handleTournamentSelect(t.id))}
                  >
                    <span>
                      {t.name} {t.year ? `(${t.year})` : ""}
                      <span className="adminpanel-badge">
                        {t.numberOfTeams} {(t.teamSize || 1) === 1 ? "Spieler" : "Teams"}{(t.teamSize || 1) > 1 ? ` · ${t.teamSize} Spieler/Team` : ""}
                      </span>
                    </span>
                    <div className="adminpanel-actions">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteTournament(t.id);
                        }}
                        className="delete-button adminpanel-delete-btn"
                      >
                        Löschen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {selectedTournamentId && (
              <>
                <section className="adminpanel-card">
                  <h2>Gruppen</h2>
                  <div className="adminpanel-inline-row">
                    <select
                      value={selectedGroupId || ""}
                      onChange={(e) => handleGroupSelect(e.target.value ? Number(e.target.value) : null)}
                      className="groupphase-select"
                    >
                      <option value="">Gruppe wählen ...</option>
                      {(Array.isArray(groups) ? groups : []).map((g) => {
                        const teamCount = Array.isArray(g.teamIds || g.playerIds)
                          ? (g.teamIds || g.playerIds).length
                          : 0;
                        return (
                          <option key={g.id} value={g.id}>
                            {g.name}
                            {teamCount > 0 && ` (${teamCount} Teams)`}
                          </option>
                        );
                      })}
                    </select>
                    <input
                      type="text"
                      placeholder="Neue Gruppe Name"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      className="groupphase-input"
                    />
                    <button onClick={createGroup} className="groupphase-button">
                      Anlegen
                    </button>
                  </div>
                  <ul className="adminpanel-list">
                    {(Array.isArray(groups) ? groups : []).map((g) => {
                      const teamCount = Array.isArray(g.teamIds || g.playerIds)
                        ? (g.teamIds || g.playerIds).length
                        : 0;
                      return (
                        <li
                          key={g.id}
                          className={`adminpanel-list-item ${selectedGroupId === g.id ? "active" : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleGroupSelect(g.id)}
                          onKeyDown={(event) => handleListItemKeyDown(event, () => handleGroupSelect(g.id))}
                        >
                          <span>
                            {g.name}
                            {teamCount > 0 && (
                              <span className="adminpanel-badge"> - {teamCount} {teamsLabel}</span>
                            )}
                          </span>
                          <div className="adminpanel-actions">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                deleteGroup(g.id);
                              }}
                              className="delete-button adminpanel-delete-btn"
                            >
                              Löschen
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>

                {selectedGroup && (
                  <section className="adminpanel-card">
                    <h2>{teamsLabel} in {selectedGroup.name}</h2>
                    <div className="adminpanel-inline-row">
                      {getAvailableTeamsForGroup(selectedGroup).length === 0 ? (
                        <div className="adminpanel-hint">
                          Es sind keine freien {teamsLabel} verfügbar. Bitte zuerst {teamsLabel} dem Turnier zuordnen
                          oder aus anderen Gruppen entfernen.
                        </div>
                      ) : (
                        <>
                          <select
                            value={selectedTeamToAddToGroup}
                            onChange={(e) => setSelectedTeamToAddToGroup(e.target.value)}
                            className="groupphase-select"
                          >
                            <option value="">{teamLabel} zuordnen ...</option>
                            {getAvailableTeamsForGroup(selectedGroup).map((team) => (
                              <option key={team.id} value={team.id}>
                                {getTeamLabel(team)}
                              </option>
                            ))}
                          </select>
                          <button
                            className="groupphase-button"
                            onClick={() => selectedTeamToAddToGroup && addTeamToGroup(selectedGroup.id, Number(selectedTeamToAddToGroup))}
                          >
                            Hinzufügen
                          </button>
                        </>
                      )}
                    </div>
                    <ul className="groupphase-list adminpanel-compact-list">
                      {(selectedGroup.teamIds || selectedGroup.playerIds || []).map((teamId) => {
                        const team = tournamentTeams.find((entry) => entry.id === teamId);
                        if (!team) return null;
                        return (
                          <li key={teamId} className="adminpanel-chip-row">
                            <span className="adminpanel-chip">{getTeamLabel(team)}</span>
                            <button
                              className="groupphase-button adminpanel-chip-remove"
                              title={`${teamLabel} aus Gruppe entfernen`}
                              onClick={() => removeTeamFromGroup(selectedGroup.id, teamId)}
                            >
                              Entfernen
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {groupAssignmentError && (
                      <p className="adminpanel-warning-text" role="alert">
                        {groupAssignmentError}
                      </p>
                    )}
                  </section>
                )}

                {selectedTournamentId && (Array.isArray(groups) ? groups : []).length > 0 && (
                  <section className="adminpanel-card">
                    <h2>Automatische Verteilung</h2>
                    <p className="adminpanel-status-text">
                      Zugeordnet: {tournamentTeams.length - unassignedTournamentTeams.length} von {tournamentTeams.length} {teamsLabel}
                      {unassignedTournamentTeams.length > 0 && ` · ${unassignedTournamentTeams.length} noch offen`}
                    </p>
                    <div className="adminpanel-inline-row adminpanel-inline-row--actions">
                      <button
                        onClick={() => distributeTeams(true)}
                        className="groupphase-button"
                        disabled={unassignedTournamentTeams.length === 0}
                      >
                        Freie {teamsLabel} verteilen
                      </button>
                      <button
                        onClick={() => distributeTeams(false)}
                        className="groupphase-button"
                        disabled={tournamentTeams.length === 0}
                      >
                        Alle {teamsLabel} neu verteilen
                      </button>
                      <button
                        onClick={() => exportGroupTablesPdf(groups, tournamentTeams, selectedTournament?.name, api)}
                        className="groupphase-button secondary"
                        disabled={groups.length === 0}
                      >
                        Paarungstabellen als PDF
                      </button>
                    </div>
                    {unassignedTournamentTeams.length === 0 && tournamentTeams.length > 0 && (
                      <p className="adminpanel-hint">
                        Aktuell sind bereits alle Turnier{teamsLabel.toLowerCase()} einer Gruppe zugeordnet.
                      </p>
                    )}
                    {distributionMessage && (
                      <p className={`adminpanel-hint ${distributionError ? "adminpanel-warning-text" : ""}`}>
                        {distributionMessage}
                      </p>
                    )}
                  </section>
                )}
              </>
            )}
          </div>

          <div className="adminpanel-column adminpanel-column--right">
            {!selectedTournament && (
              <section className="adminpanel-card adminpanel-empty-state">
                <h2>Kein Turnier ausgewählt</h2>
                <p>Bitte wählen Sie links ein Turnier aus, um Teams und Gruppen zu verwalten.</p>
              </section>
            )}

            {selectedTournament && (
              <>
                <section className="adminpanel-card">
                  <h2>Turnierinformationen</h2>
                  <p><strong>Name:</strong> {selectedTournament.name}</p>
                  <p><strong>Jahr:</strong> {selectedTournament.year}</p>
                  <p><strong>Platzgruppe:</strong> {selectedTournament.courtGroupName || selectedTournament.courtGroupId}</p>
                  <p><strong>Spieler pro Team:</strong> {selectedTournamentTeamSize}</p>
                  <p><strong>Meldeschluss:</strong> {selectedTournament.registrationDeadline || "-"}</p>
                  <p><strong>Ende:</strong> {selectedTournament.endDate || "-"}</p>
                </section>

                {!selectedTournament.started && (
                  <section className="adminpanel-card">
                    <h2>Turnierdaten bearbeiten</h2>
                    <div className="adminpanel-form">
                      <label className="adminpanel-field">
                        <span>Name</span>
                        <input
                          type="text"
                          value={editTournamentData.name}
                          onChange={(e) => setEditTournamentData((prev) => ({...prev, name: e.target.value}))}
                          className="groupphase-input"
                        />
                      </label>
                      <label className="adminpanel-field">
                        <span>Jahr</span>
                        <input
                          type="number"
                          min="1900"
                          max="2100"
                          value={editTournamentData.year}
                          onChange={(e) => setEditTournamentData((prev) => ({...prev, year: e.target.value}))}
                          className="groupphase-input"
                        />
                      </label>
                      <label className="adminpanel-field">
                        <span>Beschreibung</span>
                        <input
                          type="text"
                          value={editTournamentData.description}
                          onChange={(e) => setEditTournamentData((prev) => ({...prev, description: e.target.value}))}
                          className="groupphase-input"
                        />
                      </label>
                      <label className="adminpanel-field">
                        <span>Platzgruppe</span>
                        <select
                          value={editTournamentData.courtGroupId}
                          onChange={(e) => setEditTournamentData((prev) => ({...prev, courtGroupId: e.target.value}))}
                          className="groupphase-select"
                        >
                          <option value="">Platzgruppe wählen ...</option>
                          {courtGroups.map((cg) => (
                            <option key={cg.id} value={cg.id}>
                              {cg.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="adminpanel-field">
                        <span>Meldeschluss (optional)</span>
                        <input
                          type="date"
                          value={editTournamentData.registrationDeadline}
                          onChange={(e) => setEditTournamentData((prev) => ({...prev, registrationDeadline: e.target.value}))}
                          className="groupphase-input"
                        />
                      </label>
                      <label className="adminpanel-field">
                        <span>Enddatum</span>
                        <input
                          type="date"
                          value={editTournamentData.endDate}
                          onChange={(e) => setEditTournamentData((prev) => ({...prev, endDate: e.target.value}))}
                          className="groupphase-input"
                        />
                      </label>
                    </div>
                    <button
                      className="groupphase-button primary"
                      onClick={saveTournamentData}
                      disabled={savingTournament}
                    >
                      {savingTournament ? "Speichere ..." : "Turnierdaten speichern"}
                    </button>
                  </section>
                )}

                {!selectedTournament.started && (
                  <section className="adminpanel-card">
                    <h2>Turnier starten</h2>
                    {(selectedTournament.numberOfIncompleteTeams ?? 0) > 0 && (
                      <p className="adminpanel-warning-text" role="alert">
                        ⚠ {selectedTournament.numberOfIncompleteTeams} unvollständige{selectedTournament.numberOfIncompleteTeams === 1 ? "s Team" : " Teams"} vorhanden – Turnier kann erst gestartet werden, wenn alle Teams vollständig sind.
                      </p>
                    )}
                    <label className="adminpanel-field">
                      <span>Anzahl Gruppen</span>
                      <input
                        type="number"
                        min={2}
                        value={selectedTournament.numberOfGroups || 4}
                        onChange={(e) => updateNumberOfGroups(selectedTournamentId, Number(e.target.value))}
                        className="groupphase-input"
                        style={{ maxWidth: 120 }}
                      />
                    </label>
                    <p className="adminpanel-hint">
                      Die Qualifikanten-Anzahl pro Gruppe und das Spiel um Platz 3 werden weiter unten
                      in der Sektion "K.O.-Phase" konfiguriert, sobald das Turnier gestartet ist.
                    </p>
                    <button
                      className="groupphase-button primary"
                      onClick={() => startTournament(selectedTournamentId)}
                      style={{ marginTop: 12 }}
                    >
                      Turnier starten
                    </button>
                  </section>
                )}

                <section className="adminpanel-card">
                  <h2>Neuer {teamLabel} für das Turnier</h2>
                  <p className="adminpanel-hint">
                    Wählen Sie genau {selectedTournamentTeamSize} {selectedTournamentTeamSize === 1 ? "Spieler" : "Spieler"} aus.
                  </p>
                  <div className="adminpanel-inline-row">
                    {!isSingles && (
                      <input
                        type="text"
                        placeholder="Optionaler Teamname"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        className="groupphase-input"
                      />
                    )}
                    <button onClick={createTeamForTournament} className="groupphase-button">
                      {teamLabel} anlegen
                    </button>
                  </div>
                  <div className="adminpanel-inline-row" style={{flexWrap: "wrap", marginTop: 12}}>
                    {selectedTeamPlayerIds.length === 0 ? (
                      <span className="adminpanel-hint">Noch keine TeamPlayer ausgewählt.</span>
                    ) : (
                      selectedTeamPlayerIds.map((teamPlayerId) => {
                        const teamPlayer = teamPlayers.find((entry) => entry.id === teamPlayerId);
                        if (!teamPlayer) return null;
                        return (
                          <button
                            key={teamPlayerId}
                            type="button"
                            className="adminpanel-chip"
                            onClick={() => toggleTeamPlayerSelection(teamPlayerId)}
                          >
                            {getTeamPlayerLabel(teamPlayer)} ×
                          </button>
                        );
                      })
                    )}
                  </div>
                  {availableTeamPlayers.length === 0 ? (
                    <p className="adminpanel-hint" style={{marginTop: 12}}>
                      Keine freien Spieler verfügbar. Alle Spieler sind bereits dem Turnier zugeordnet.
                    </p>
                  ) : (
                    <>
                    <input
                      type="text"
                      placeholder="Nach Name filtern…"
                      value={teamPlayerSearch}
                      onChange={(e) => setTeamPlayerSearch(e.target.value)}
                      className="groupphase-input"
                      style={{marginTop: 12, width: "100%"}}
                    />
                    <ul className="adminpanel-list adminpanel-list--compact" style={{marginTop: 8}}>
                      {availableTeamPlayers.filter((teamPlayer) =>
                        !teamPlayerSearch || (teamPlayer.name || "").toLowerCase().includes(teamPlayerSearch.toLowerCase())
                      ).map((teamPlayer) => {
                        const isSelected = selectedTeamPlayerIds.includes(teamPlayer.id);
                        const isDisabled = !isSelected && selectedTeamPlayerIds.length >= selectedTournamentTeamSize;
                        return (
                          <li key={teamPlayer.id} className="adminpanel-list-item">
                            <label style={{display: "flex", alignItems: "center", gap: 8, width: "100%"}}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => toggleTeamPlayerSelection(teamPlayer.id)}
                              />
                              <span>{getTeamPlayerLabel(teamPlayer)}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                    </>
                  )}
                </section>

                <section className="adminpanel-card">
                  <h2>Turnier{teamsLabel.toLowerCase()}</h2>
                  <TeamList
                    teams={tournamentTeams}
                    teamSize={selectedTournamentTeamSize}
                    onRemove={removeTeamFromTournament}
                    onRename={renameTeam}
                  />
                </section>

                <section className="adminpanel-card">
                  <h2>K.O.-Phase</h2>
                  <div className="adminpanel-status-box">
                    {knockoutPhaseExists[selectedTournamentId] ? (
                      <div>
                        <div className="adminpanel-status active">
                          <span>✓</span>
                          <span>K.O.-Phase aktiv</span>
                        </div>
                        <p className="adminpanel-status-text">
                          Die K.O.-Phase ist initialisiert. Gruppenphase abgeschlossen.
                        </p>
                        <div className="adminpanel-inline-row" style={{ flexWrap: "wrap" }}>
                          <button
                            onClick={() => recreateKnockoutMatches(selectedTournamentId)}
                            className="groupphase-button"
                          >
                            Alle K.O.-Spiele neu anlegen
                          </button>
                          <button
                            onClick={() => reopenGroupPhase(selectedTournamentId)}
                            className="groupphase-button adminpanel-danger-btn"
                          >
                            Gruppenphase wieder öffnen
                          </button>
                        </div>
                        <p className="adminpanel-hint">
                          "Neu anlegen" löscht nur die K.O.-Spiele und legt sie sofort wieder anhand
                          der aktuellen Seed-Konfiguration an (bereits abgeschlossene Gruppen bleiben
                          abgeschlossen). Fragt nur nach, wenn bereits ein K.O.-Ergebnis eingetragen wurde.
                        </p>
                        <p className="adminpanel-warning-text">
                          "Gruppenphase wieder öffnen" löscht zusätzlich alle Gruppen-Abschlüsse!
                        </p>
                      </div>
                    ) : (
                      <div>
                        {knockoutBracketPlanned[selectedTournamentId] ? (
                          <>
                            <div className="adminpanel-status">
                              <span>✓</span>
                              <span>K.O.-Baum angelegt</span>
                            </div>
                            <p className="adminpanel-status-text">
                              Der K.O.-Baum ist angelegt, die Gruppenphase läuft noch. Gruppen (Team-Zuordnung,
                              Qualifikanten, Seeding) können nicht mehr geändert werden.
                            </p>
                            <button
                              onClick={() => deleteKnockoutBracket(selectedTournamentId)}
                              className="groupphase-button adminpanel-danger-btn"
                            >
                              K.O.-Baum löschen
                            </button>
                            <p className="adminpanel-hint">
                              Löscht den K.O.-Baum wieder. Gruppen können danach wieder geändert werden.
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="adminpanel-status">
                              <span>○</span>
                              <span>K.O.-Baum noch nicht angelegt</span>
                            </div>
                            <p className="adminpanel-status-text">
                              {selectedTournament.started
                                ? "Gruppen können noch geändert werden, solange der K.O.-Baum nicht angelegt ist."
                                : "Turnier zuerst starten, um Gruppen zu befüllen und den K.O.-Baum anzulegen."}
                            </p>
                            <label className="adminpanel-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                              <input
                                type="checkbox"
                                checked={Boolean(selectedTournament.thirdPlaceMatch)}
                                disabled={bracketSizeOfSelectedTournament < 4 && !directFinalFormatEligibleOfSelectedTournament}
                                onChange={(e) => updateThirdPlaceMatch(selectedTournamentId, e.target.checked)}
                              />
                              <span>Spiel um Platz 3 austragen</span>
                            </label>
                            <p className="adminpanel-hint">
                              {bracketSizeOfSelectedTournament < 4 && !directFinalFormatEligibleOfSelectedTournament
                                ? "Erfordert mindestens ein Halbfinale (Summe Qualifikanten ≥ 4)."
                                : directFinalFormatEligibleOfSelectedTournament
                                ? "Direktfinale-Format (2 Gruppen à 1 Qualifikant): Die Gruppenersten spielen direkt das Finale, die Gruppenzweiten direkt um Platz 3 - kein Halbfinale nötig."
                                : "Die beiden Halbfinal-Verlierer spielen zusätzlich um Platz 3."}
                            </p>
                            <KnockoutSeedConfig
                              tournamentId={selectedTournamentId}
                              numberOfGroups={selectedTournament.numberOfGroups}
                              onBracketSizeChange={setBracketSizeOfSelectedTournament}
                              onDirectFinalFormatEligibleChange={setDirectFinalFormatEligibleOfSelectedTournament}
                            />
                            {selectedTournament.started && (
                              <button
                                onClick={() => buildKnockoutBracket(selectedTournamentId)}
                                className="groupphase-button primary"
                                style={{ marginTop: 12 }}
                              >
                                K.O.-Baum anlegen
                              </button>
                            )}
                          </>
                        )}
                        {selectedTournament.started && !groupPhaseClosed && (
                          <button
                            onClick={() => closeGroupPhase(selectedTournamentId)}
                            className="groupphase-button primary"
                            style={{ marginTop: 12 }}
                          >
                            Gruppenphase abschließen & K.O.-Phase öffnen
                          </button>
                        )}
                        {selectedTournament.started && !groupPhaseClosed && !allGroupsCompleted && (
                          <p className="adminpanel-hint">
                            Hinweis: Noch {openGroupMatchCount} von {totalGroupMatchCount} Gruppenspielen ohne Ergebnis.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </section>

              </>
            )}
          </div>
        </div>

      {isCreateTournamentModalOpen && (
        <div className="modal-backdrop adminpanel-modal-backdrop" onClick={() => setIsCreateTournamentModalOpen(false)}>
          <div
            className="modal-dialog adminpanel-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-tournament-title"
          >
            <div className="modal-header adminpanel-modal-header">
              <h2 id="create-tournament-title">Neues Turnier anlegen</h2>
              <button
                type="button"
                className="modal-close-button adminpanel-modal-close"
                onClick={() => setIsCreateTournamentModalOpen(false)}
                aria-label="Schließen"
              >
                ×
              </button>
            </div>
            <div className="modal-body adminpanel-modal-body">
              <div className="adminpanel-form">
                <label className="adminpanel-field">
                  <span>Name</span>
                  <input
                    type="text"
                    placeholder="Turniername"
                    value={newTournamentName}
                    onChange={(e) => setNewTournamentName(e.target.value)}
                    className="groupphase-input"
                  />
                </label>
                <label className="adminpanel-field">
                  <span>Jahr</span>
                  <input
                    type="number"
                    min="1900"
                    max="2100"
                    value={newTournamentYear}
                    onChange={(e) => setNewTournamentYear(e.target.value)}
                    className="groupphase-input"
                  />
                </label>
                <label className="adminpanel-field">
                  <span>Spieler pro Team</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={newTournamentTeamSize}
                    onChange={(e) => setNewTournamentTeamSize(e.target.value)}
                    className="groupphase-input"
                  />
                </label>
                <label className="adminpanel-field">
                  <span>Platzgruppe</span>
                  <select
                    value={selectedCourtGroupId}
                    onChange={(e) => setSelectedCourtGroupId(e.target.value)}
                    className="groupphase-select"
                  >
                    <option value="">Platzgruppe wählen ...</option>
                    {courtGroups.map((cg) => (
                      <option key={cg.id} value={cg.id}>
                        {cg.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="adminpanel-field">
                  <span>Meldeschluss (optional)</span>
                  <input
                    type="date"
                    value={newTournamentRegistrationDeadline}
                    onChange={(e) => setNewTournamentRegistrationDeadline(e.target.value)}
                    className="groupphase-input"
                  />
                </label>
                <label className="adminpanel-field">
                  <span>Enddatum</span>
                  <input
                    type="date"
                    value={newTournamentEndDate}
                    onChange={(e) => setNewTournamentEndDate(e.target.value)}
                    className="groupphase-input"
                  />
                </label>
                <label className="adminpanel-field">
                  <span>Anzahl Gruppen</span>
                  <select
                    value={newTournamentNumberOfGroups}
                    onChange={(e) => setNewTournamentNumberOfGroups(Number(e.target.value))}
                    className="groupphase-select"
                  >
                    <option value={2}>2 Gruppen</option>
                    <option value={4}>4 Gruppen</option>
                    <option value={8}>8 Gruppen</option>
                  </select>
                </label>
              </div>
            </div>
            <div className="modal-footer adminpanel-modal-footer">
              <button
                type="button"
                className="groupphase-button secondary"
                onClick={() => setIsCreateTournamentModalOpen(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="groupphase-button primary"
                onClick={async () => {
                  const created = await createTournament();
                  if (created) {
                    setIsCreateTournamentModalOpen(false);
                  }
                }}
              >
                Anlegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentManagement;
